import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "icon", "output"]
  static values = {
    state: { type: String, default: "idle" },
    append: { type: Boolean, default: true }
  }

  connect() {
    this.mediaRecorder = null
    this.audioChunks = []
    this.audioContext = null
    this.analyser = null
    this.animationFrame = null
    this.abortController = null
    this.cancelled = false
    this.boundKeyHandler = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.cancel()
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    }
  }

  async toggle() {
    if (this.stateValue === "recording") {
      this.stopRecording()
    } else if (this.stateValue === "processing") {
      this.cancel()
    } else if (this.stateValue === "idle") {
      await this.startRecording()
    }
  }

  cancel() {
    this.cancelled = true

    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }

    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop()
    }

    this.cleanupAudioContext()
    this.stateValue = "idle"
    document.removeEventListener("keydown", this.boundKeyHandler)
  }

  async startRecording() {
    try {
      this.cancelled = false
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.stream = stream
      this.setupAudioAnalyser(stream)

      this.mediaRecorder = new MediaRecorder(stream, { mimeType: this.getSupportedMimeType() })
      this.audioChunks = []

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data)
        }
      }

      this.mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop())
        this.cleanupAudioContext()
        if (!this.cancelled) {
          this.processAudio()
        }
      }

      this.mediaRecorder.start()
      this.stateValue = "recording"
      this.startVisualization()
      document.addEventListener("keydown", this.boundKeyHandler)
    } catch (error) {
      console.error("Error accessing microphone:", error)
      window.showToast?.("No se pudo acceder al micrófono")
    }
  }

  setupAudioAnalyser(stream) {
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    this.analyser = this.audioContext.createAnalyser()
    this.analyser.fftSize = 256
    this.analyser.smoothingTimeConstant = 0.8
    const source = this.audioContext.createMediaStreamSource(stream)
    source.connect(this.analyser)
  }

  startVisualization() {
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount)
    document.documentElement.classList.add("voice-recording-active")
    this.showRecordingHint()

    const visualize = () => {
      if (this.stateValue !== "recording") return

      this.analyser.getByteFrequencyData(dataArray)
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length
      const normalized = average / 255

      const scale = 1 + normalized * 0.4
      const opacity = 0.3 + normalized * 0.7

      this.buttonTarget.style.transform = `scale(${scale})`
      this.buttonTarget.style.boxShadow = `0 0 ${normalized * 20}px rgba(198, 93, 78, ${opacity})`

      document.documentElement.style.setProperty("--voice-intensity", normalized.toFixed(2))

      this.animationFrame = requestAnimationFrame(visualize)
    }

    visualize()
  }

  cleanupAudioContext() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = null
    }
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
    this.buttonTarget.style.transform = ""
    this.buttonTarget.style.boxShadow = ""
    document.documentElement.classList.remove("voice-recording-active")
    document.documentElement.style.removeProperty("--voice-intensity")
    this.hideRecordingHint()
  }

  showRecordingHint() {
    if (document.getElementById("voice-recording-hint")) return

    const hint = document.createElement("div")
    hint.id = "voice-recording-hint"
    hint.className = "fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-2 px-4 py-2 bg-charcoal-mid/90 border border-terracotta/50 font-mono text-xs text-cream/70 backdrop-blur-sm"
    hint.innerHTML = `
      <span class="inline-block w-2 h-2 bg-terracotta rounded-full animate-pulse"></span>
      <span>Grabando...</span>
      <kbd class="px-1.5 py-0.5 bg-charcoal border border-cream/20 text-cream/50 text-[10px]">ESC</kbd>
      <span class="text-cream/40">cancelar</span>
    `
    document.body.appendChild(hint)
  }

  hideRecordingHint() {
    document.getElementById("voice-recording-hint")?.remove()
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop()
    }
    document.removeEventListener("keydown", this.boundKeyHandler)
  }

  async processAudio() {
    this.stateValue = "processing"
    this.abortController = new AbortController()

    const audioBlob = new Blob(this.audioChunks, { type: this.getSupportedMimeType() })
    const formData = new FormData()
    formData.append("audio", audioBlob, `recording.${this.getFileExtension()}`)

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']").content
      const response = await fetch("/transcriptions", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: formData,
        signal: this.abortController.signal
      })

      if (response.ok) {
        const data = await response.json()
        this.fillOutput(data.text)
        window.showToast?.("Transcripción completada")
      } else {
        const data = await response.json()
        window.showToast?.(data.error || "Error en la transcripción")
      }
    } catch (error) {
      if (error.name === "AbortError") {
        window.showToast?.("Cancelado")
      } else {
        console.error("Error processing audio:", error)
        window.showToast?.("Error al procesar el audio")
      }
    } finally {
      this.abortController = null
      this.stateValue = "idle"
    }
  }

  fillOutput(text) {
    if (!this.hasOutputTarget) return

    const output = this.outputTarget
    if (this.appendValue && output.value.trim()) {
      output.value = output.value.trim() + "\n\n" + text
    } else {
      output.value = text
    }

    output.dispatchEvent(new Event("input", { bubbles: true }))
    output.focus()
  }

  stateValueChanged() {
    this.updateUI()
  }

  updateUI() {
    const button = this.buttonTarget
    const icon = this.iconTarget

    button.classList.remove("bg-terracotta", "bg-gold", "text-cream")

    switch (this.stateValue) {
      case "recording":
        button.classList.add("bg-terracotta", "text-cream")
        icon.innerHTML = this.stopIcon
        break
      case "processing":
        button.classList.add("bg-gold", "text-charcoal")
        icon.innerHTML = this.spinnerIcon
        break
      default:
        icon.innerHTML = this.micIcon
    }
  }

  getSupportedMimeType() {
    const types = ["audio/webm", "audio/mp4", "audio/ogg"]
    return types.find(type => MediaRecorder.isTypeSupported(type)) || "audio/webm"
  }

  getFileExtension() {
    const mimeType = this.getSupportedMimeType()
    const extensions = { "audio/webm": "webm", "audio/mp4": "m4a", "audio/ogg": "ogg" }
    return extensions[mimeType] || "webm"
  }

  get micIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
    </svg>`
  }

  get stopIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
      <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z" />
    </svg>`
  }

  get spinnerIcon() {
    return `<svg class="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>`
  }
}
